import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { LogIn, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { setCurrentUserRole } from '@/utils/userRole';
import { Brand } from '@/components/brand/Brand';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Where to land after successful login.
  // Preference order: location.state.from (set by ProtectedRoute) > ?redirect=... > '/'
  const getPostLoginDestination = () => {
    const fromState = location.state?.from;
    if (fromState && fromState.pathname) {
      return `${fromState.pathname}${fromState.search || ''}${fromState.hash || ''}`;
    }
    const params = new URLSearchParams(location.search);
    const redirect = params.get('redirect');
    if (redirect && redirect.startsWith('/')) {
      return redirect;
    }
    return '/';
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/auth/login`, {
        email: formData.email,
        password: formData.password
      });

      if (response.data.success) {
        // Store JWT tokens
        if (response.data.access_token) {
          localStorage.setItem('accessToken', response.data.access_token);
        }
        if (response.data.refresh_token) {
          localStorage.setItem('refreshToken', response.data.refresh_token);
        }
        // Store token expiration time
        if (response.data.expires_in) {
          const expiresAt = Date.now() + (response.data.expires_in * 1000);
          localStorage.setItem('tokenExpiresAt', expiresAt.toString());
        }
        
        // Store user and company info
        localStorage.setItem('userId', response.data.user.id);
        localStorage.setItem('userEmail', response.data.user.email);
        localStorage.setItem('userName', `${response.data.user.first_name} ${response.data.user.last_name}`);
        // Persist role through the canonical helper (also fires keyMadeRoleChanged
        // for any open RoleContext to re-render).
        setCurrentUserRole(response.data.user.role);
        
        if (response.data.company) {
          localStorage.setItem('companyId', response.data.company.id);
          localStorage.setItem('companyName', response.data.company.name);
          localStorage.setItem('companySlug', response.data.company.slug);
        }

        // Iter 291 — fire inbound contact-sync pull on login (fire-and-forget).
        // Any errors will surface in the ContactSyncSettings sync log.
        try {
          const backend = process.env.REACT_APP_BACKEND_URL;
          // LG-1 — fire-and-forget, and it must STAY that way: a 401 on this
          // call would otherwise reach the shared response interceptor, which
          // clears the session and hard-redirects to /login — wiping the
          // sign-in that just succeeded. `.catch()` cannot stop that; the
          // interceptor's own per-request opt-out can.
          axios.post(`${backend}/api/contact-sync/pull-now`, {}, {
            headers: { Authorization: `Bearer ${response.data.access_token}` },
            _skipAuthRedirect: true,
          }).catch(() => {});
        } catch { /* non-fatal */ }
        
        // Verify the just-issued token round-trips through /me BEFORE
        // navigating away. This closes the race where PublicOrAuthGate
        // would fire its own /me, get a transient 401, silently clear
        // the tokens, and bounce the user back to /login with no toast.
        //
        // Single retry with a short delay absorbs the transient
        // backend-replica / clock-skew window that produced the
        // "first login fails, second works" pattern.
        // LG-1 — a ceiling per attempt. /auth/me had none, and a browser will
        // not time out a stalled request on its own: the button span forever,
        // nothing navigated, and no message was ever shown. Six seconds, twice,
        // and then we go anyway — see below.
        const VERIFY_CEILING_MS = 6000;
        const verifyToken = async () => {
          const headers = { Authorization: `Bearer ${response.data.access_token}` };
          for (let attempt = 0; attempt < 2; attempt += 1) {
            try {
              const meRes = await axios.get(`${API}/api/auth/me`, {
                headers,
                validateStatus: () => true,
                timeout: VERIFY_CEILING_MS,
                _skipAuthRedirect: true,
              });
              if (meRes.status >= 200 && meRes.status < 300) return { ok: true };
              if (meRes.status === 401 && attempt === 0) {
                await new Promise((r) => setTimeout(r, 300));
                continue;
              }
              return { ok: false, status: meRes.status, body: meRes.data };
            } catch (netErr) {
              const timedOut = netErr.code === 'ECONNABORTED'
                || /timeout/i.test(netErr.message || '');
              // A stall is not a transient 401: asking again only spends the
              // ceiling twice. Six seconds is the whole budget.
              if (timedOut) return { ok: false, status: 0, timedOut: true, body: netErr.message };
              if (attempt === 0) {
                await new Promise((r) => setTimeout(r, 300));
                continue;
              }
              return { ok: false, status: 0, timedOut, body: netErr.message };
            }
          }
          return { ok: false, status: 0, body: 'unreachable' };
        };
        const verification = await verifyToken();
        // The login was a 200 and the tokens are stored: a verification that
        // only ran out of time proves nothing about them, so go to the app and
        // let the destination page's gate verify. Stranding someone on the
        // login screen is the worse answer.
        if (!verification.ok && verification.timedOut) {
          navigate(getPostLoginDestination(), { replace: true });
          return;
        }
        if (!verification.ok) {
          // Don't strand the user — clear tokens and surface what
          // actually happened. Two retries already absorbed transient
          // hiccups; a failure now is real.
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('tokenExpiresAt');
          localStorage.removeItem('userId');
          localStorage.removeItem('companyId');
          const reason = verification.status === 401
            ? 'Token issued but rejected on verification. Please try again — if this keeps happening, contact support.'
            : verification.status === 0
              ? `Could not reach the server to verify your login (${verification.body || 'network error'}). Please try again.`
              : `Login verification failed (HTTP ${verification.status}). Please try again.`;
          // LG-1 — long enough to be read: this is the only account of why
          // the app did not open.
          toast.error(reason, { duration: 8000 });
          return;
        }

        toast.success(`Welcome back, ${response.data.user.first_name}!`);
        navigate(getPostLoginDestination(), { replace: true });
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDFCFB] to-[#F5F3F0] flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 flex items-center justify-center mb-4">
            <Brand size="lg" variant="mark" testId="login-logo" />
          </div>
          <CardTitle className="text-2xl font-bold text-[#1A1A1A]">
            Welcome to KEY-MADE
          </CardTitle>
          <CardDescription className="text-gray-600">
            Sign in to your account
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#70170F]" />
                Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@yourcompany.com"
                className="border-gray-300 focus:border-[#70170F] focus:ring-[#70170F]"
                required
                autoComplete="email"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-[#70170F]" />
                  Password
                </Label>
                <Link to="/forgot-password" data-testid="login-forgot-password-link" className="text-sm text-[#70170F] hover:underline">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  className="border-gray-300 focus:border-[#70170F] focus:ring-[#70170F] pr-10"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="login-toggle-password-visibility"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 min-w-[32px] min-h-[32px] flex items-center justify-center text-gray-500 hover:text-[#70170F] focus:outline-none focus:ring-2 focus:ring-[#70170F] rounded"
                  tabIndex={0}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </CardContent>

          <CardFooter className="flex flex-col gap-4">
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-[#70170F] hover:bg-[#5a1209] text-white"
            >
              {loading ? (
                'Signing in...'
              ) : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign In
                </>
              )}
            </Button>

            <p className="text-sm text-gray-600 text-center">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#70170F] hover:underline font-medium">
                Sign up
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
